import {
  Selector,
  State,
  BundleState,
  BundleStatus,
  ModuleState,
  ModuleStatus,
  ExportOfModule,
  SystemPublicState,
  ModuleWithError,
  MissingDependenciesMap,
} from './types';
import { SystemInvariant } from './SystemInvariant';
import { InfiniteLoopInDependenciesResolution, InfiniteLoopInCircularDependenciesPathResolution } from './errors';

export class SystemSelector<
  BundleName extends string,
  ModuleName extends string,
  BundlesModules extends { [K in BundleName]: ModuleName },
  ModulesExport extends { [K in ModuleName]: any }
> {
  /**
   * General
   */

  private resolvedTopology: { [M in ModuleName]: BundleName } | null = null;

  constructor(private invariant: SystemInvariant<BundleName, ModuleName, BundlesModules, ModulesExport>) {}

  isBooted(s: State<BundleName, ModuleName, BundlesModules, ModulesExport>) {
    return s.booted;
  }

  publicSystemState(): Selector<BundleName, ModuleName, BundlesModules, ModulesExport, SystemPublicState<ModuleName>> {
    return (state, select): SystemPublicState<ModuleName> => {
      const modulesStatus = Object.keys(state.modules).reduce((acc, mod) => {
        acc[mod] = select(this.moduleStatus(mod as ModuleName), state);
        return acc;
      }, {});
      return {
        modulesStatus,
        modulesReasons: Object.keys(modulesStatus)
          // .filter(mod => modulesStatus[mod] !== ModuleStatus.Loaded)
          .reduce((acc, mod) => {
            acc[mod] = select(this.moduleAllDependencies(mod as ModuleName), state);
            return acc;
          }, {}),
      };
    };
  }

  modulesNames(s: State<BundleName, ModuleName, BundlesModules, ModulesExport>): Array<ModuleName> {
    return s.topology.modules;
  }

  bundleOfModule<M extends ModuleName>(
    moduleName: M
  ): (s: State<BundleName, ModuleName, BundlesModules, ModulesExport>) => BundleName {
    return (s: State<BundleName, ModuleName, BundlesModules, ModulesExport>): BundleName => {
      // create resolvedTopology only once
      if (this.resolvedTopology === null) {
        this.resolvedTopology = Object.keys(s.topology.bundles).reduce<{ [N in ModuleName]: BundleName }>(
          (acc, bundleName) => {
            const modules: Array<ModuleName> = s.topology.bundles[bundleName];
            modules.forEach(mod => {
              acc[mod as any] = bundleName;
            });
            return acc;
          },
          {} as any
        );
      }
      try {
        // return the bundleName
        return this.invariant.notUndefined(this.invariant.notNull(this.resolvedTopology)[moduleName]);
      } catch (error) {
        console.error(error);
        throw new Error(`Can't find bundle of module ${moduleName} !`);
      }
    };
  }

  modulesOfBundles<B extends BundleName>(
    bundleName: B
  ): (s: State<BundleName, ModuleName, BundlesModules, ModulesExport>) => Array<ModuleName> {
    return (s: State<BundleName, ModuleName, BundlesModules, ModulesExport>): Array<ModuleName> => {
      const bundle = this.invariant.notUndefined(Object.keys(s.topology.bundles).find(bunName => bunName === bunName));
      return this.invariant.notUndefined(s.topology.bundles[bundle]);
    };
  }

  dependenciesOfModule<M extends ModuleName>(
    moduleName: M
  ): (s: State<BundleName, ModuleName, BundlesModules, ModulesExport>) => Array<ModuleName> {
    return (s: State<BundleName, ModuleName, BundlesModules, ModulesExport>): Array<ModuleName> => {
      return s.topology.dependencies[moduleName] || [];
    };
  }

  /**
   * Bundle
   */

  bundleState<B extends BundleName>(bundleName: B) {
    return (
      s: State<BundleName, ModuleName, BundlesModules, ModulesExport>
    ): BundleState<BundleName, ModuleName, BundlesModules, ModulesExport, B> => {
      const bState = s.bundles[bundleName];
      if (bState) {
        return bState as BundleState<BundleName, ModuleName, BundlesModules, ModulesExport, B>;
      }
      // Void bundle state
      return {
        fetching: null,
        fetched: null,
        parsing: null,
        parsed: null,
        error: null,
      };
    };
  }

  bundleStatus<B extends BundleName>(
    bundleName: B
  ): Selector<BundleName, ModuleName, BundlesModules, ModulesExport, BundleStatus> {
    return (s, select): BundleStatus => {
      const bState = select(this.bundleState(bundleName), s);
      if (bState.error !== null) {
        return BundleStatus.Error;
      }
      if (bState.parsed !== null) {
        return BundleStatus.Parsed;
      }
      if (bState.parsing !== null) {
        return BundleStatus.Parsing;
      }
      if (bState.fetched !== null) {
        return BundleStatus.Fetched;
      }
      if (bState.fetching !== null) {
        return BundleStatus.Fetching;
      }
      return BundleStatus.Void;
    };
  }

  bundleHasFetched<B extends BundleName>(
    bundleName: B
  ): Selector<BundleName, ModuleName, BundlesModules, ModulesExport, boolean> {
    return (s, select): boolean => {
      return select(this.bundleState(bundleName), s).fetched !== null;
    };
  }

  bundleHasParsed<B extends BundleName>(
    bundleName: B
  ): Selector<BundleName, ModuleName, BundlesModules, ModulesExport, boolean> {
    return (s, select): boolean => {
      return select(this.bundleState(bundleName), s).parsed !== null;
    };
  }

  /**
   * Module
   */

  moduleState<M extends ModuleName>(moduleName: M) {
    return (
      s: State<BundleName, ModuleName, BundlesModules, ModulesExport>
    ): ModuleState<ModuleName, ModulesExport, M> => {
      const mState: ModuleState<ModuleName, ModulesExport, M> | undefined = s.modules[moduleName];
      if (mState !== undefined) {
        return mState;
      }
      return {
        loading: null,
        loadingReason: null,
        loaded: null,
        dependencies: [],
        missingDependencies: {},
        error: null,
      };
    };
  }

  moduleStatus<M extends ModuleName>(
    moduleName: M
  ): Selector<BundleName, ModuleName, BundlesModules, ModulesExport, ModuleStatus> {
    return (s, select): ModuleStatus => {
      const bundleName = select(this.bundleOfModule(moduleName), s);
      const bStatus = select(this.bundleStatus(bundleName), s);
      const mState = select(this.moduleState(moduleName), s);
      if (mState.error !== null) {
        return ModuleStatus.Error;
      }
      if (mState.loaded !== null) {
        return ModuleStatus.Loaded;
      }
      if (mState.loading !== null) {
        return ModuleStatus.Loading;
      }

      switch (bStatus) {
        case BundleStatus.Error:
          return ModuleStatus.Error;
        case BundleStatus.Void:
          return ModuleStatus.Void;
        case BundleStatus.Fetching:
          return ModuleStatus.Fetching;
        case BundleStatus.Fetched:
          return ModuleStatus.Fetched;
        case BundleStatus.Parsing:
          return ModuleStatus.Parsing;
        case BundleStatus.Parsed:
          // if (mState.error !== null) {
          //   return ModuleStatus.Error;
          // }
          // if (mState.loaded !== null) {
          //   return ModuleStatus.Loaded;
          // }
          // if (mState.loading !== null) {
          //   return ModuleStatus.Loading;
          // }
          return ModuleStatus.Parsed;
        default:
          return this.invariant.unreachable();
      }
    };
  }

  moduleStatusReason<M extends ModuleName>(
    moduleName: M
  ): Selector<BundleName, ModuleName, BundlesModules, ModulesExport, { [K in ModuleName]?: ModuleStatus }> {
    return (s, select): { [K in ModuleName]?: ModuleStatus } => {
      const status = select(this.moduleStatus(moduleName), s);
      if (status !== ModuleStatus.Loading) {
        return {};
      }
      const allDeps = select(this.moduleAllDependencies(moduleName), s);
      const allDepsStatus = allDeps.reduce<{ [K in ModuleName]?: ModuleStatus }>((acc, mod) => {
        const modStatus = select(this.moduleStatus(mod), s);
        if (modStatus !== ModuleStatus.Parsed) {
          acc[mod] = modStatus;
        }
        return acc;
      }, {});
      return allDepsStatus;
    };
  }

  moduleLoading<M extends ModuleName>(
    moduleName: M
  ): Selector<
    BundleName,
    ModuleName,
    BundlesModules,
    ModulesExport,
    Promise<ExportOfModule<ModuleName, ModulesExport, M>>
  > {
    return (s, select): Promise<ExportOfModule<ModuleName, ModulesExport, M>> => {
      return this.invariant.notNull(select(this.moduleState(moduleName), s).loading);
    };
  }

  moduleError<M extends ModuleName>(
    moduleName: M
  ): Selector<BundleName, ModuleName, BundlesModules, ModulesExport, any> {
    return (s, select): any => {
      return select(this.moduleState(moduleName), s).error;
    };
  }

  moduleHasLoaded<M extends ModuleName>(
    moduleName: M
  ): Selector<BundleName, ModuleName, BundlesModules, ModulesExport, boolean> {
    return (s, select): boolean => {
      return select(this.moduleState(moduleName), s).loaded !== null;
    };
  }

  moduleExport<M extends ModuleName>(
    moduleName: M
  ): Selector<BundleName, ModuleName, BundlesModules, ModulesExport, ExportOfModule<ModuleName, ModulesExport, M>> {
    return (s, select): ExportOfModule<ModuleName, ModulesExport, M> => {
      return this.invariant.notNull(select(this.moduleState(moduleName), s).loaded);
    };
  }

  /**
   * Dependencies
   */

  moduleAllDependencies(
    moduleName: ModuleName
  ): Selector<BundleName, ModuleName, BundlesModules, ModulesExport, Array<ModuleName>> {
    return (s, select) => {
      let maxLoop = 500;
      const uniq = <T>(items: Array<T>) => items.filter((item, pos, arr) => arr.indexOf(item) === pos);
      const getAllDeps = (deps: Array<ModuleName>): Array<ModuleName> => {
        maxLoop--;
        if (maxLoop < 0) {
          throw new InfiniteLoopInDependenciesResolution(moduleName, deps);
        }
        const newDeps = deps
          .map(dep => select(this.missingDependencies(dep), s))
          .reduce((acc, depsDeps) => acc.concat(depsDeps), []);

        const allDeps = uniq([...deps, ...newDeps]);
        if (allDeps.length === deps.length) {
          return allDeps;
        }
        return getAllDeps(allDeps);
      };
      const moduleDeps = select(this.missingDependencies(moduleName), s);
      return getAllDeps(moduleDeps);
    };
  }

  moduleHasCircularDependencies(
    moduleName: ModuleName
  ): Selector<BundleName, ModuleName, BundlesModules, ModulesExport, boolean> {
    return (s, select) => {
      return select(this.moduleAllDependencies(moduleName), s).indexOf(moduleName) > -1;
    };
  }

  findCircularDependenciesPath(
    moduleName: ModuleName
  ): Selector<BundleName, ModuleName, BundlesModules, ModulesExport, Array<ModuleWithError<ModuleName>> | null> {
    return (s, select) => {
      let maxLoop = 500;
      const findPath = (
        targetModule: ModuleName,
        currentModule: ModuleWithError<ModuleName> | null = null,
        pastModules: Array<ModuleWithError<ModuleName>> = []
      ): Array<ModuleWithError<ModuleName>> | null => {
        maxLoop--;
        if (maxLoop < 0) {
          throw new InfiniteLoopInCircularDependenciesPathResolution(moduleName);
        }
        const currentModuleName = currentModule === null ? targetModule : currentModule.name;
        const deps = select(this.missingDependenciesMap(currentModuleName), s);
        const pastNames = pastModules.map(mod => mod.name);
        return Object.keys(deps).reduce((acc: null | Array<ModuleWithError<ModuleName>>, depName) => {
          if (acc !== null) {
            // already resolved, skip
            return acc;
          }
          const current: ModuleWithError<ModuleName> = {
            name: depName as any,
            error: deps[depName],
          };
          if (depName === targetModule) {
            // resolve
            return [...pastModules, current];
          }
          if (pastNames.indexOf(depName as any) > -1) {
            return null;
          }
          return findPath(targetModule, current, [...pastModules, current]);
        }, null);
      };
      const path = findPath(moduleName);
      return path ? path.reverse() : path;
    };
  }

  missingDependenciesMap(
    moduleName: ModuleName
  ): Selector<BundleName, ModuleName, BundlesModules, ModulesExport, MissingDependenciesMap<ModuleName>> {
    return (s, select) => {
      return select(this.moduleState(moduleName), s).missingDependencies
        ? this.invariant.notUndefined(select(this.moduleState(moduleName), s).missingDependencies)
        : {};
    };
  }

  missingDependencies(
    moduleName: ModuleName
  ): Selector<BundleName, ModuleName, BundlesModules, ModulesExport, Array<ModuleName>> {
    return (s, select) => {
      return Object.keys(select(this.missingDependenciesMap(moduleName), s)) as Array<ModuleName>;
    };
  }

  moduleHasMissingDependency(
    moduleName: ModuleName,
    needModuleName: ModuleName
  ): Selector<BundleName, ModuleName, BundlesModules, ModulesExport, boolean> {
    return (s, select): boolean => {
      const mState = select(this.moduleState(moduleName), s);
      if (mState.missingDependencies && mState.missingDependencies[needModuleName]) {
        return true;
      }
      return false;
    };
  }
}



// WEBPACK FOOTER //
// ./node_modules/front-system/engine/SystemSelector.ts